import { create } from 'zustand'
import { persist } from 'zustand/middleware'
import type { ShortSurveyData, SkinType, Sensitivity, SkinCondition } from '@/types/survey'

interface SurveyState {
  // Survey data
  shortSurvey: ShortSurveyData
  currentStep: number
  totalSteps: number

  // Actions
  setAge: (age: number) => void
  setSkinType: (skinType: SkinType) => void
  setSensitivity: (sensitivity: Sensitivity) => void
  setSkinConditions: (conditions: SkinCondition[]) => void
  toggleSkinCondition: (condition: SkinCondition) => void
  setShortSurvey: (survey: Partial<ShortSurveyData>) => void

  // Navigation
  nextStep: () => void
  prevStep: () => void
  goToStep: (step: number) => void

  // Validation
  isCurrentStepValid: () => boolean
  isComplete: () => boolean

  // Reset
  resetSurvey: () => void
}

const initialSurveyData: ShortSurveyData = {
  age: null,
  skinType: null,
  sensitivity: null,
  skinConditions: [],
}

export const useSurveyStore = create<SurveyState>()(
  persist(
    (set, get) => ({
      shortSurvey: initialSurveyData,
      currentStep: 0,
      totalSteps: 4,

      setAge: (age) =>
        set((state) => ({
          shortSurvey: { ...state.shortSurvey, age },
        })),

      setSkinType: (skinType) =>
        set((state) => ({
          shortSurvey: { ...state.shortSurvey, skinType },
        })),

      setSensitivity: (sensitivity) =>
        set((state) => ({
          shortSurvey: { ...state.shortSurvey, sensitivity },
        })),

      setSkinConditions: (skinConditions) =>
        set((state) => ({
          shortSurvey: { ...state.shortSurvey, skinConditions },
        })),

      toggleSkinCondition: (condition) =>
        set((state) => {
          const current = state.shortSurvey.skinConditions ?? []

          // If selecting "none", clear all others
          if (condition === 'none') {
            return {
              shortSurvey: {
                ...state.shortSurvey,
                skinConditions: current.includes('none') ? [] : ['none'],
              },
            }
          }

          // If selecting any other condition, remove "none" if present
          const withoutNone = current.filter((c) => c !== 'none')

          if (withoutNone.includes(condition)) {
            // Remove the condition
            return {
              shortSurvey: {
                ...state.shortSurvey,
                skinConditions: withoutNone.filter((c) => c !== condition),
              },
            }
          } else {
            // Add the condition
            return {
              shortSurvey: {
                ...state.shortSurvey,
                skinConditions: [...withoutNone, condition],
              },
            }
          }
        }),

      setShortSurvey: (survey) =>
        set((state) => ({
          shortSurvey: { ...state.shortSurvey, ...survey },
        })),

      nextStep: () =>
        set((state) => ({
          currentStep: Math.min(state.currentStep + 1, state.totalSteps - 1),
        })),

      prevStep: () =>
        set((state) => ({
          currentStep: Math.max(state.currentStep - 1, 0),
        })),

      goToStep: (step) =>
        set((state) => ({
          currentStep: Math.max(0, Math.min(step, state.totalSteps - 1)),
        })),

      isCurrentStepValid: () => {
        const { shortSurvey, currentStep } = get()
        switch (currentStep) {
          case 0: // Age
            return shortSurvey.age !== null && shortSurvey.age >= 16 && shortSurvey.age <= 100
          case 1: // Skin Type
            return shortSurvey.skinType !== null
          case 2: // Sensitivity
            return shortSurvey.sensitivity !== null
          case 3: // Skin Conditions
            return (shortSurvey.skinConditions ?? []).length > 0
          default:
            return false
        }
      },

      isComplete: () => {
        const { shortSurvey } = get()
        return (
          shortSurvey.age !== null &&
          shortSurvey.age >= 16 &&
          shortSurvey.skinType !== null &&
          shortSurvey.sensitivity !== null &&
          (shortSurvey.skinConditions ?? []).length > 0
        )
      },

      resetSurvey: () =>
        set({
          shortSurvey: initialSurveyData,
          currentStep: 0,
        }),
    }),
    {
      name: 'k-skin-survey',
      partialize: (state) => ({
        shortSurvey: state.shortSurvey,
        currentStep: state.currentStep,
      }),
      // Migration: ensure old localStorage data gets new fields
      merge: (persistedState, currentState) => {
        const persisted = persistedState as Partial<SurveyState> | undefined
        if (!persisted) return currentState

        // Ensure skinConditions exists (added in survey revision)
        const shortSurvey = {
          ...initialSurveyData,
          ...persisted.shortSurvey,
          skinConditions: persisted.shortSurvey?.skinConditions ?? [],
        }

        // Clamp currentStep to valid range (survey was reduced from 8 to 4 steps)
        const maxStep = currentState.totalSteps - 1
        const currentStep = Math.min(Math.max(0, persisted.currentStep ?? 0), maxStep)

        return {
          ...currentState,
          ...persisted,
          shortSurvey,
          currentStep,
        }
      },
    }
  )
)
